import {
  searchExtractions,
  searchFileReview,
  type ReviewExtractionInDb,
  type FileReviewInDb,
} from '@/api';
import { defineStore } from 'pinia';
import { logger } from '@/utils/logger';

interface FileReviewState {
  current: FileReviewInDb | null;
  byReview: Record<string, FileReviewInDb[]>;
  extractions: Record<string, Record<string, ReviewExtractionInDb[]> | undefined>;
}

export const useFileReviewStore = defineStore('fileReview', {
  state: (): FileReviewState => ({
    current: null,
    byReview: {},
    extractions: {},
  }),
  getters: {
    fileReviewsFor:
      state =>
      (reviewUid: string): FileReviewInDb[] =>
        state.byReview[reviewUid] ?? [],
    extractionsFor:
      state =>
      (reviewUid: string, fileReviewUid: string): ReviewExtractionInDb[] =>
        state.extractions[reviewUid]?.[fileReviewUid] ?? [],
    allExtractionsFor:
      state =>
      (reviewUid: string): Record<string, ReviewExtractionInDb[]> =>
        state.extractions[reviewUid] ?? {},
  },
  actions: {
    async loadFileReview(reviewUid: string): Promise<FileReviewInDb | null> {
      const response = await searchFileReview({
        path: { review_uid: reviewUid },
        body: { text: '' },
      });

      if (!response.data) {
        throw new Error('Failed to fetch file review');
      }
      const result = response.data;
      const fileReview = result.results[0] || null;
      this.current = fileReview;
      return fileReview;
    },
    async searchFileReviews(
      reviewUid: string,
      searchParams: { text: string }
    ): Promise<FileReviewInDb[]> {
      const response = await searchFileReview({
        path: { review_uid: reviewUid },
        body: searchParams,
      });

      if (!response.data) {
        throw new Error('Failed to fetch file reviews');
      }
      const result = response.data;
      const fileReviews = result.results;
      this.byReview[reviewUid] = fileReviews;
      return fileReviews;
    },
    async loadExtractions(
      reviewUid: string,
      fileReviewUid: string
    ): Promise<ReviewExtractionInDb[]> {
      const response = await searchExtractions({
        path: {
          review_uid: reviewUid,
          file_review_uid: fileReviewUid,
        },
        body: { text: '' },
      });

      if (!response.data) {
        throw new Error('Failed to fetch extractions');
      }
      const result = response.data;
      const extractions = result.results;
      this.extractions[reviewUid] ??= {};
      this.extractions[reviewUid][fileReviewUid] = extractions;
      return extractions;
    },
    /**
     * Reload a review's file reviews, and their extractions.
     *
     * Pass `fileReviewUid` to reload just that one — a view showing a single
     * document has no use for the other N files' extractions, and reloading
     * them all was costing `1 + N` requests on every websocket event.
     */
    async refresh(reviewUid: string, fileReviewUid?: string): Promise<void> {
      try {
        await this.searchFileReviews(reviewUid, { text: '' });
        const fileReviews = this.fileReviewsFor(reviewUid);
        const targets = fileReviewUid
          ? fileReviews.filter(fileReview => fileReview.uid === fileReviewUid)
          : fileReviews;
        await Promise.all(
          targets.map(fileReview => this.loadExtractions(reviewUid, fileReview.uid))
        );
      } catch (error) {
        logger.error('[FileReviewStore] Failed to refresh for review:', error);
      }
    },

    /**
     * Updates a specific extraction in the local state
     * @param reviewUid - The UUID of the review
     * @param fileReviewUid - The UUID of the file review
     * @param extractionUid - The UUID of the extraction to update
     * @param updatedExtraction - The updated extraction data
     */
    updateExtraction(
      reviewUid: string,
      fileReviewUid: string,
      extractionUid: string,
      updatedExtraction: ReviewExtractionInDb
    ): void {
      if (!this.extractions[reviewUid]?.[fileReviewUid]) {
        return;
      }

      const extractionIndex = this.extractions[reviewUid][fileReviewUid].findIndex(
        extraction => extraction.uid === extractionUid
      );

      if (extractionIndex !== -1) {
        this.extractions[reviewUid][fileReviewUid][extractionIndex] = updatedExtraction;
      }
    },
  },
});
